import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Upload, FileText, FileImage, File as FileIcon, Download, Trash2, Filter, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "contract", label: "Contract" },
  { value: "invoice", label: "Invoice" },
  { value: "floor_plan", label: "Floor plan" },
  { value: "inspiration", label: "Inspiration" },
  { value: "other", label: "Other" },
];

function iconFor(ct) {
  if (!ct) return FileIcon;
  if (ct.startsWith("image/")) return FileImage;
  if (ct === "application/pdf" || ct.startsWith("text/")) return FileText;
  return FileIcon;
}
function fmtSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function DocumentsTab({ eventId, members, canEdit, previewAs = null }) {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("other");
  const [memberId, setMemberId] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/events/${eventId}/documents`); setDocs(data); }
    catch (e) { toast.error("Could not load documents"); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      if (memberId) fd.append("member_id", memberId);
      await api.post(`/events/${eventId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this document?")) return;
    try { await api.delete(`/events/${eventId}/documents/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error("Could not delete"); }
  };

  const toggleShared = async (d) => {
    try {
      await api.patch(`/events/${eventId}/documents/${d.id}`, { is_shared: !d.is_shared });
      setDocs(docs.map(x => x.id === d.id ? { ...x, is_shared: !x.is_shared } : x));
      toast.success(!d.is_shared ? "Shared with client" : "No longer shared");
    } catch (e) { toast.error("Could not update"); }
  };

  const download = async (d) => {
    try {
      const resp = await api.get(`/events/${eventId}/documents/${d.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  // When the owner is previewing as another member, apply the same scoping
  // the backend would enforce for that role on GET /events/{id}/documents:
  //   - vendor      → only docs tied to their member_id
  //   - client      → only docs marked is_shared
  //   - coordinator → sees everything (like the owner)
  const roleScoped = useMemo(() => {
    if (!previewAs) return docs;
    if (previewAs.role === "vendor") return docs.filter(d => d.member_id === previewAs.id);
    if (previewAs.role === "client") return docs.filter(d => d.is_shared);
    return docs;
  }, [docs, previewAs]);
  const filtered = useMemo(
    () => roleScoped.filter(d => filter === "all" || d.category === filter),
    [roleScoped, filter],
  );
  const nonClientMembers = useMemo(
    () => members.filter(m => m.role !== "client"),
    [members],
  );

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">
            MOD.DOCS / CONTRACTS
            {previewAs && (
              <span className="ml-2 text-[#F59E0B]" data-testid="doc-preview-scope">
                · SCOPED FOR {previewAs.role.toUpperCase()}
              </span>
            )}
          </div>
          <h2 className="font-display text-xl md:text-2xl font-bold text-app tracking-tight leading-none">
            <span className="font-mono tabular-nums">{roleScoped.length.toString().padStart(2, "0")}</span> {previewAs ? "visible to them" : "files"}
            {previewAs && docs.length !== roleScoped.length && (
              <span className="ml-2 font-mono text-xs text-muted font-normal">of {docs.length} total</span>
            )}
          </h2>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-40 h-10 rounded-sm" data-testid="doc-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={memberId || "none"} onValueChange={(v) => setMemberId(v === "none" ? "" : v)}>
              <SelectTrigger className="w-40 h-10 rounded-sm" data-testid="doc-member-select"><SelectValue placeholder="Tie to vendor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Event-wide</SelectItem>
                {nonClientMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <input ref={fileRef} type="file" onChange={onFile} className="hidden" data-testid="doc-file-input" />
            <Button data-testid="upload-doc-btn" onClick={onPick} disabled={uploading} className="rounded-sm bg-app hover:bg-[#000000]">
              <Upload size={16} strokeWidth={1.5} className="mr-2" /> {uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={13} strokeWidth={1.5} className="text-muted" />
        {[["all","All"], ...CATEGORIES.map(c => [c.value, c.label])].map(([v,l]) => (
          <button
            key={v}
            onClick={() => setFilter(v)}
            data-testid={`doc-filter-${v}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm ${filter === v ? "bevel-btn-primary text-white" : "text-muted hover:bg-surface-2"}`}
          >{l}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bevel border-hair rounded-sm p-12 text-center">
          <div className="font-serif text-2xl text-app mb-2">
            {previewAs ? `Nothing here for ${previewAs.name}.` : "Nothing here yet."}
          </div>
          <p className="text-muted font-light">
            {previewAs?.role === "vendor" && "Documents tied to this vendor will appear here. Upload contracts, invoices, or floor plans and tie them to them from the planner view."}
            {previewAs?.role === "client" && "Only documents you've explicitly shared with the client show up here. Toggle the share icon on any doc to add it."}
            {!previewAs && "Upload contracts, invoices, floor plans, and inspiration images to keep everything with the event."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(d => {
            const Icon = iconFor(d.content_type);
            const vendor = members.find(m => m.id === d.member_id);
            return (
              <div key={d.id} data-testid={`document-card-${d.id}`} className="bevel border-hair rounded-sm p-5 card-lift flex items-start gap-4">
                <div className="w-11 h-11 rounded-sm bg-surface-2 flex items-center justify-center shrink-0">
                  <Icon size={20} strokeWidth={1.5} className="text-app" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="eyebrow mb-1 flex items-center gap-2">
                    <span>{CATEGORIES.find(c => c.value === d.category)?.label || d.category}{vendor ? ` · ${vendor.name}` : ""}</span>
                    {d.is_shared && (
                      <span className="px-1.5 py-0.5 bg-surface-2 text-[#059669] rounded-sm text-[10px]">Shared</span>
                    )}
                  </div>
                  <div className="font-medium text-app truncate" title={d.original_filename}>{d.original_filename}</div>
                  <div className="text-xs text-muted font-light mt-1">{fmtSize(d.size)} · {new Date(d.created_at).toLocaleDateString(undefined, { month:"short", day:"numeric", year:"numeric" })}</div>
                </div>
                <div className="flex flex-col gap-1">
                  {canEdit && (
                    <button
                      onClick={() => toggleShared(d)}
                      data-testid={`toggle-share-${d.id}`}
                      className={`p-1 ${d.is_shared ? "text-[#059669]" : "text-muted hover:text-app"}`}
                      title={d.is_shared ? "Un-share from client" : "Share with client"}
                    >
                      {d.is_shared ? <Eye size={14} strokeWidth={1.5} /> : <EyeOff size={14} strokeWidth={1.5} />}
                    </button>
                  )}
                  <button onClick={() => download(d)} data-testid={`download-doc-${d.id}`} className="text-muted hover:text-app p-1" title="Download">
                    <Download size={14} strokeWidth={1.5} />
                  </button>
                  {canEdit && (
                    <button onClick={() => del(d.id)} data-testid={`delete-doc-${d.id}`} className="text-muted hover:text-[#EF4444] p-1" title="Delete">
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
